import { Link } from "@tanstack/react-router";
import { ArrowLeft, Check, Pencil, UploadCloud, X } from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArchVizApp } from "@/components/archviz/ArchVizApp";
import { VizHeaderControls } from "@/components/archviz/VizHeaderControls";
import { CopyPromptButton } from "@/components/solutions/CopyPromptButton";
import { MediaDialog } from "@/components/solutions/MediaDialog";
import { ShareLinksDialog } from "@/components/solutions/ShareLinksDialog";
import { UploadVersionDialog } from "@/components/solutions/UploadVersionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useVersionComments } from "@/hooks/use-comments";
import { useSolution, useUpdateSolution } from "@/hooks/use-solutions";
import type { ArchitectureData } from "@/lib/arch-parser";
import { useSession } from "@/lib/auth-client";

function EditableName({ slug, name }: { slug: string; name: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  const update = useUpdateSolution(slug);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (!next || next === name) {
      setEditing(false);
      setDraft(name);
      return;
    }
    update.mutate(
      { name: next },
      {
        onSuccess: () => setEditing(false),
      },
    );
  };

  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={update.isPending}
          className="h-7 text-base"
          aria-label="Solution name"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={commit}
          disabled={update.isPending}
          aria-label="Save name"
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={cancel}
          disabled={update.isPending}
          aria-label="Cancel rename"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-base truncate flex items-center gap-1.5 group hover:text-primary text-left"
      title="Rename solution"
    >
      <span className="truncate">{name}</span>
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 shrink-0 transition-opacity" />
    </button>
  );
}

function SolutionHeader({
  slug,
  name,
  versionNumber,
  versionLabel,
  // The breadcrumb/search/constraints cluster reads from ArchVizContext, so it
  // can only render inside <ArchVizApp> (the populated branch). The empty-state
  // branch renders this header outside that provider — pass false there.
  showVizControls = true,
}: {
  slug: string;
  name: string;
  versionNumber: number | null;
  versionLabel: string | null;
  showVizControls?: boolean;
}) {
  return (
    <header className="flex items-center gap-3 px-4 py-2.5 border-b bg-card/80 backdrop-blur">
      <div className="flex items-center gap-3 min-w-0 shrink-0">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/" aria-label="Back to solutions">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0">
          <EditableName slug={slug} name={name} />
          {versionNumber !== null && (
            <div className="text-[11px] text-muted-foreground font-mono">
              v{versionNumber}
              {versionLabel ? ` · ${versionLabel}` : ""}
            </div>
          )}
        </div>
        {showVizControls && (
          <span className="h-4 w-px bg-border max-md:hidden" aria-hidden="true" />
        )}
      </div>
      {showVizControls && <VizHeaderControls />}
    </header>
  );
}

export function SolutionPage({ slug }: { slug: string }) {
  const { data, isLoading, isError } = useSolution(slug);
  const { data: session } = useSession();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);

  const arch = useMemo(() => {
    const parsed = data?.currentVersion?.parsedData;
    return parsed ? (parsed as ArchitectureData) : null;
  }, [data]);

  const versionId = data?.currentVersion?.id ?? null;
  const commentsQuery = useVersionComments(versionId);

  const { commentCounts, carryOverCounts } = useMemo(() => {
    const own = new Map<string, number>();
    const carry = new Map<string, number>();
    for (const c of commentsQuery.data?.own ?? []) {
      if (c.resolved) continue;
      own.set(c.elementId, (own.get(c.elementId) ?? 0) + 1);
    }
    for (const c of commentsQuery.data?.carriedOver ?? []) {
      carry.set(c.elementId, (carry.get(c.elementId) ?? 0) + 1);
    }
    return {
      commentCounts: Object.fromEntries(own),
      carryOverCounts: Object.fromEntries(carry),
    };
  }, [commentsQuery.data]);

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Could not load solution.</p>
          <Button variant="outline" asChild>
            <Link to="/">Back to solutions</Link>
          </Button>
        </div>
      </div>
    );
  }

  const versionNumber = data.currentVersion?.versionNumber ?? null;
  const versionLabel = data.currentVersion?.label ?? null;

  // TODO: expand when collaborator/team permissions arrive — currently single-owner + admin override
  const canWrite =
    !!session?.user && (data.createdBy === session.user.id || session.user.role === "admin");

  if (!arch) {
    return (
      <div className="min-h-screen flex flex-col">
        <SolutionHeader
          slug={slug}
          name={data.name}
          versionNumber={versionNumber}
          versionLabel={versionLabel}
          showVizControls={false}
        />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md text-center space-y-4">
            <h2 className="text-2xl">No architecture yet</h2>
            <p className="text-sm text-muted-foreground">
              Upload an <code className="font-mono">architecture.md</code> file generated by the
              Claude Code export prompt to see this solution come alive.
            </p>
            <div className="flex items-center justify-center gap-2">
              <CopyPromptButton solutionName={data.name} />
              <Button onClick={() => setUploadOpen(true)}>
                <UploadCloud className="h-4 w-4 mr-1.5" />
                Upload architecture
              </Button>
            </div>
          </div>
        </div>
        <UploadVersionDialog
          slug={slug}
          solutionName={data.name}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
        />
        <ShareLinksDialog slug={slug} open={shareOpen} onOpenChange={setShareOpen} />
        <MediaDialog slug={slug} open={mediaOpen} onOpenChange={setMediaOpen} />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <ArchVizApp
        architecture={arch}
        readOnly
        showThemeToggle={false}
        extras={{
          versionId,
          commentCounts,
          carryOverCounts,
          currentUserId: session?.user.id ?? null,
          solutionSlug: slug,
          canWrite,
          onUpload: () => setUploadOpen(true),
          onShare: () => setShareOpen(true),
          onMedia: () => setMediaOpen(true),
        }}
        header={
          <SolutionHeader
            slug={slug}
            name={data.name}
            versionNumber={versionNumber}
            versionLabel={versionLabel}
          />
        }
      />
      <UploadVersionDialog
        slug={slug}
        solutionName={data.name}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
      <ShareLinksDialog slug={slug} open={shareOpen} onOpenChange={setShareOpen} />
      <MediaDialog slug={slug} open={mediaOpen} onOpenChange={setMediaOpen} />
    </div>
  );
}
